// ─────────────────────────────────────────────
// KLOTHIQ — Supplier List Page (wired)
// ─────────────────────────────────────────────

import { Input } from '@/components/ui/FormControls';
import { useState } from 'react';
import Link from '@/components/common/AccessLink';
import { useSuppliers } from '@/hooks/useSuppliers';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import ExportActions from '@/components/common/ExportActions';
import { Plus, Truck, Search, Pencil, Phone, ChevronRight } from 'lucide-react';

export default function SupplierListPage() {
  const [search, setSearch] = useState('');
  const { data: suppliers, isLoading, error, refetch } = useSuppliers(search);

  const filterSummaryText = search ? `Search: "${search}"` : 'All Suppliers';

  return (
    <div>
      <PageHeader
        title="Suppliers"
        description="Manage your product suppliers"
        actions={
          <div className="flex items-center gap-2">
            <ExportActions
              excelOptions={() => ({
                fileName: `Suppliers_List_${new Date().toISOString().split('T')[0]}`,
                sheetName: 'Suppliers',
                filtersSummary: filterSummaryText,
                columns: [
                  { header: 'Supplier Name',    key: 'name' },
                  { header: 'Contact Person',   key: 'contact_person' },
                  { header: 'Phone',            key: 'phone' },
                  { header: 'Email',            key: 'email' },
                  { header: 'City',             key: 'city' },
                ],
                data: (suppliers ?? []).map((s) => ({
                  name:           s.name,
                  contact_person: (s as any).contact_person ?? '—',
                  phone:          s.phone ?? '—',
                  email:          (s as any).email ?? '—',
                  city:           s.city ?? '—',
                })),
              })}
              pdfOptions={() => ({
                fileName: `Suppliers_List_${new Date().toISOString().split('T')[0]}`,
                title: 'Supplier Directory Report',
                subtitle: `Exported on ${new Date().toLocaleDateString('en-IN')}`,
                filtersSummary: filterSummaryText,
                columns: [
                  { header: 'Supplier Name',  key: 'name' },
                  { header: 'Contact Person', key: 'contact_person' },
                  { header: 'Phone',          key: 'phone' },
                  { header: 'City',           key: 'city' },
                ],
                rows: (suppliers ?? []).map((s) => ({
                  name:           s.name,
                  contact_person: (s as any).contact_person ?? '—',
                  phone:          s.phone ?? '—',
                  city:           s.city ?? '—',
                })),
                summaryLines: [
                  { label: 'Total Suppliers', value: (suppliers ?? []).length.toString() },
                ],
              })}
            />
            <Link to="/suppliers/new" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90">
              <Plus className="h-4 w-4" /> Add Supplier
            </Link>
          </div>
        }
      />

      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="supplier-search"
            type="text"
            placeholder="Search suppliers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            helpLabel="Search records"
          />
        </div>
      </div>

      {isLoading && <LoadingState message="Loading suppliers…" />}
      {error && <ErrorState message={error.message} onRetry={refetch} />}

      {!isLoading && !error && suppliers?.length === 0 && (
        <EmptyState
          icon={<Truck className="h-12 w-12" />}
          title={search ? 'No suppliers match your search' : 'No suppliers yet'}
          description={search ? 'Try a different search term' : 'Add your first supplier to start recording purchases'}
          action={!search ? (
            <Link to="/suppliers/new" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
              <Plus className="h-4 w-4" /> Add Supplier
            </Link>
          ) : undefined}
        />
      )}

      {!isLoading && !error && suppliers && suppliers.length > 0 && (
        <>
          {/* Desktop */}
          <div className="obsidian-surface hidden overflow-hidden rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Phone</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">City</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Contact</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {suppliers.map((s) => (
                  <tr key={s.id} className="transition-colors hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {s.phone ? (
                        <a href={`tel:${s.phone}`} className="flex items-center gap-1 hover:text-primary">
                          <Phone className="h-3 w-3" />{s.phone}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{s.city ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.contact_person ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/suppliers/${s.id}/edit`} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="obsidian-surface divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-sm md:hidden">
            {suppliers.map((s) => (
              <Link key={s.id} to={`/suppliers/${s.id}/edit`} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30">
                <div>
                  <p className="font-medium text-foreground">{s.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {s.phone ?? 'No phone'}{s.city ? ` · ${s.city}` : ''}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}
          </p>
        </>
      )}
    </div>
  );
}
